import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Star, BadgeCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/context/SiteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Stars = ({ value, size = "w-3.5 h-3.5", testid }) => (
  <span className="inline-flex gap-0.5" data-testid={testid} aria-label={`${value} / 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-[#B5A642] text-[#B5A642]" : "text-[#B5A642]/35"}`} strokeWidth={1.25} />
    ))}
  </span>
);

const fmtDate = (iso, lang) =>
  new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" });

const ReviewEntry = ({ r }) => {
  const { t, lang } = useLang();
  const dog = [r.dog_name, r.dog_breed].filter(Boolean).join(", ");
  return (
    <li className="py-5 border-b border-[#303B2D]/60" data-testid="review-entry" data-no-translate>
      <div className="flex items-center justify-between gap-3">
        <Stars value={r.rating} />
        <span className="font-ledger text-[11px] text-[#D3CEC4]/40">{fmtDate(r.created_at, lang)}</span>
      </div>
      <p className="mt-3 font-display text-lg leading-relaxed text-[#E8DDC7]/90">“{r.note}”</p>
      <p className="mt-2 text-[12px] text-[#D3CEC4]/55">
        {r.first_name}{dog && <span className="italic"> · {t("for", "pour")} {dog}</span>}
        {r.verified && (
          <span className="ml-2 inline-flex items-center gap-1 text-[#B5A642]/80" data-testid="review-verified">
            <BadgeCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("Verified buyer", "Acheteur vérifié")}
          </span>
        )}
      </p>
    </li>
  );
};

const field = "w-full bg-transparent border border-[#303B2D] rounded-sm px-3 py-2.5 text-[14px] text-[#E8DDC7] placeholder:text-[#D3CEC4]/35 focus:outline-none focus:border-[#B5A642]/70 transition-colors";

const ReviewForm = ({ productId, onDone, initialEmail = "" }) => {
  const { t, lang } = useLang();
  const [f, setF] = useState({ email: initialEmail, rating: 0, note: "", first_name: "", dog_name: "", dog_breed: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return setError(t("Choose a number of stars.", "Choisissez un nombre d'étoiles."));
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/reviews`, { ...f, product_id: productId, lang });
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("Please check the note is at least ten characters and the email is valid.", "Vérifiez que l'avis compte au moins dix caractères et que le courriel est valide."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-5 space-y-3" data-testid="review-form" data-no-translate>
      <div className="flex gap-1" role="radiogroup" aria-label={t("Rating", "Note")}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} data-testid={`review-star-${n}`} onClick={() => setF({ ...f, rating: n })} aria-label={`${n}`}
            className="p-0.5 transition-transform duration-200 hover:scale-110">
            <Star className={`w-6 h-6 ${n <= f.rating ? "fill-[#B5A642] text-[#B5A642]" : "text-[#B5A642]/40"}`} strokeWidth={1.25} />
          </button>
        ))}
      </div>
      <input type="email" required data-testid="review-email" value={f.email} onChange={set("email")} className={field}
        placeholder={t("The email you checked out with", "Le courriel utilisé lors de l'achat")} />
      <textarea required minLength={10} maxLength={800} rows={4} data-testid="review-note" value={f.note} onChange={set("note")} className={field}
        placeholder={t("How has it suited them?", "Comment cela leur convient-il ?")} />
      <div className="grid grid-cols-3 gap-2">
        <input required maxLength={30} data-testid="review-first-name" value={f.first_name} onChange={set("first_name")} className={field} placeholder={t("Your first name", "Votre prénom")} />
        <input maxLength={30} data-testid="review-dog-name" value={f.dog_name} onChange={set("dog_name")} className={field} placeholder={t("Dog's name", "Nom du chien")} />
        <input maxLength={40} data-testid="review-dog-breed" value={f.dog_breed} onChange={set("dog_breed")} className={field} placeholder={t("Breed", "Race")} />
      </div>
      {error && <p data-testid="review-error" className="text-[12px] text-[#C9876B]">{error}</p>}
      <button data-testid="review-submit" disabled={busy}
        className="w-full py-3 border border-[#B5A642]/70 text-[#D3CEC4] text-[11px] uppercase tracking-[0.2em] rounded-sm hover:bg-[#B5A642]/10 transition-colors disabled:opacity-50">
        {busy ? t("Entering it in the ledger…", "Inscription au registre…") : t("Submit the note", "Envoyer l'avis")}
      </button>
      <p className="text-[10px] text-[#D3CEC4]/40">
        {t("Notes from verified buyers appear once the keepers have read them. Please share experiences, not medical claims.",
          "Les avis d'acheteurs vérifiés paraissent une fois lus par les gardiens. Partagez des expériences, pas des allégations médicales.")}
      </p>
    </form>
  );
};

export const DrawerReviews = ({ product, reviewEmail = null }) => {
  const { t } = useLang();
  const { orderingOpen } = useSite();
  const [reviews, setReviews] = useState([]);
  const [mode, setMode] = useState(reviewEmail !== null ? "form" : "list");
  const ref = useRef(null);

  useEffect(() => {
    if (reviewEmail === null) return undefined;
    const id = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 700);
    return () => clearTimeout(id);
  }, [reviewEmail]);

  useEffect(() => {
    axios.get(`${API}/reviews/${product.id}`).then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [product.id]);

  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <section ref={ref} className="mt-10 pt-8 border-t border-[#3F6B48]/50" data-testid="drawer-reviews">
      <div className="flex items-baseline justify-between gap-3" data-no-translate>
        <h3 className="font-display text-2xl text-[#E8DDC7]">{t("Customer reviews", "Avis de clients")}</h3>
        {reviews.length > 0 && (
          <span className="inline-flex items-center gap-2" data-testid="drawer-reviews-average">
            <Stars value={avg} />
            <span className="font-ledger text-[12px] text-[#D3CEC4]/60">{avg.toFixed(1)} · {reviews.length}</span>
          </span>
        )}
      </div>
      {reviews.length === 0 && mode === "list" && (
        <p data-testid="drawer-reviews-empty" className="mt-3 text-[15px] text-[#E8DDC7]/85" data-no-translate>
          {t("Customer reviews will appear here after launch.", "Les avis de clients paraîtront ici après le lancement.")}
        </p>
      )}
      {reviews.length > 0 && <ul className="mt-2">{reviews.map((r) => <ReviewEntry key={r.id} r={r} />)}</ul>}
      {mode === "list" && (orderingOpen || reviewEmail !== null) && (
        <button data-testid="review-open-form" onClick={() => setMode("form")}
          className="mt-5 text-[11px] uppercase tracking-[0.2em] text-[#B5A642]/80 hover:text-[#B5A642] transition-colors" data-no-translate>
          {t("Leave a note", "Laisser un avis")}
        </button>
      )}
      {mode === "form" && <ReviewForm productId={product.id} initialEmail={reviewEmail || ""} onDone={() => setMode("thanks")} />}
      {mode === "thanks" && (
        <p data-testid="review-thanks" className="mt-5 font-display italic text-lg text-[#B5A642]" data-no-translate>
          {t("Thank you. Your note is with the keepers and will appear once they've read it.", "Merci. Votre avis est entre les mains des gardiens et paraîtra une fois lu.")}
        </p>
      )}
    </section>
  );
};
